import { Select, TextField } from 'components/forms';
import {
  LOCATION_TYPES,
  RULES_SEARCH_LOCATION_OPTIONS,
  RULES_SEARCH_LOCATION_TYPE, RULES_SEARCH_LOCATION_TYPE_VALUES,
} from 'constants/rules';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchRulesByParams, setRulesSearch } from 'store/slices/rulesSlice';
import { useAppDispatch, useAppSelector } from 'hooks/typedReduxHooks';

import getLocationOptions from 'utils/location';
import { IOption } from 'types/shared';
import { ISearchParams } from 'types/searchParams';
import { MdOutlineCancel } from 'react-icons/md';
import useSearchRules from 'hooks/useSearchRules';
import s from '../Controls.module.css';

const initialValues = {
  name: '',
  login_ad: '',
  locationType: RULES_SEARCH_LOCATION_TYPE.AGENT.toString(),
  locationValue: '',
};

type TLocationType = 1 | 3

const Search = () => {
  const [locationOptions, setLocationOptions] = useState<IOption[]>([]);
  const [locationPlaceholder, setLocationPlaceholder] = useState(
    RULES_SEARCH_LOCATION_TYPE_VALUES[RULES_SEARCH_LOCATION_TYPE.AGENT],
  );
  const [isEmptyLocationsList, setIsEmptyLocationsList] = useState(true);

  const { agents, terminals } = useAppSelector(
    (state) => state.locations,
  );

  const setAgentsParams = useCallback(() => {
    setLocationOptions(getLocationOptions(agents) || []);
    setIsEmptyLocationsList(false);
  }, [agents, setLocationOptions, setIsEmptyLocationsList]);

  useEffect(() => {
    if (agents?.length && !locationOptions?.length) {
      setAgentsParams();
    }
  }, [agents, locationOptions, setAgentsParams]);

  const { getRules } = useSearchRules();

  const dispatch = useAppDispatch();
  const {
    resetForm,
    values: formikValues,
    handleSubmit,
    handleChange,
    setFieldValue,
  } = useFormik({
    initialValues,
    onSubmit: (values: ISearchParams) => {
      const searchData: ISearchParams = {};
      if (values.name) searchData.name = values.name;
      if (values.login_ad) searchData.login_ad = values.login_ad;
      if (values.locationValue && values.locationType) {
        searchData.locationType = values.locationType;
        searchData.locationValue = values.locationValue;
      }

      if (Object.keys(searchData).length) {
        dispatch(setRulesSearch(values));
        getRules([searchData]);
      }
    },
  });

  const handleChangeLocation = useCallback(
    (value?: string | string[]) => {
      const LOCATIONS_DATA = {
        [LOCATION_TYPES.AGENTS]: agents,
        [LOCATION_TYPES.TERMINALS]: terminals,
      };
      const type: TLocationType = Number(value) as TLocationType;
      const options = getLocationOptions(LOCATIONS_DATA[type]);
      setLocationOptions(options || []);
      setLocationPlaceholder(
        RULES_SEARCH_LOCATION_TYPE_VALUES[type],
      );
      setFieldValue('locationType', value);
      setIsEmptyLocationsList(!LOCATIONS_DATA[type]?.length);
    },
    [agents, terminals, setFieldValue],
  );

  const handleSetLocationValue = useCallback(
    (value?: string | string[]) => {
      setFieldValue('locationValue', value);
    },
    [setFieldValue],
  );

  const clearSearch = useCallback(() => {
    dispatch(setRulesSearch({}));
    dispatch(fetchRulesByParams({}));
    resetForm();
    setLocationPlaceholder(
      RULES_SEARCH_LOCATION_TYPE_VALUES[RULES_SEARCH_LOCATION_TYPE.AGENT],
    );
    setAgentsParams();
  }, [resetForm, setAgentsParams, dispatch]);

  return (
    <form onSubmit={handleSubmit} className={s.searchContainer}>
      <TextField
        name="name"
        label="Наименование правила"
        labelOnFocus="Наименование правила"
        onChange={handleChange}
        value={formikValues?.name}
      />
      <TextField
        name="login_ad"
        label="Автор правила"
        labelOnFocus="Автор правила"
        onChange={handleChange}
        value={formikValues?.login_ad}
      />
      <Select
        options={RULES_SEARCH_LOCATION_OPTIONS}
        onChange={handleChangeLocation}
        placeholder="Тип поиска"
        value={formikValues?.locationType?.toString()}
      />
      <Select
        options={locationOptions}
        onChange={handleSetLocationValue}
        placeholder={locationPlaceholder}
        isClearable
        isSearchable
        isDisabled={isEmptyLocationsList}
        indicator="search"
        variant="outlined"
        value={formikValues?.locationValue?.toString()}
      />
      <div className={s.searchButtonsContainer}>
        <button type="submit" className={s.searchButton}>
          Поиск
        </button>
        {(formikValues?.name
          || formikValues.login_ad
          || formikValues.locationValue) && (
          <button
            onClick={clearSearch}
            type="button"
            className="hover:scale-[1.1] duration-100"
          >
            <MdOutlineCancel />
          </button>
        )}
      </div>
    </form>
  );
};

export default Search;
